/**
 * Plan of Record — section editor. One of the 10 canonical sections, edited as
 * narrative (markdown-friendly plain text). Section status moves draft →
 * in_review → approved independently of the PoR's own status.
 */

import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ChevronLeft, ChevronRight, Pencil, X } from 'lucide-react';
import type { AssembledPor, PorSectionStatus, PorSectionType } from '../../types/plan-of-record';
import { POR_SECTION_LABELS, POR_SECTION_PROMPTS } from '../../types/plan-of-record';
import { updateSection } from '../../lib/planOfRecordService';
import { usePorPageVisibility } from '../../hooks/usePorPageVisibility';
import {
  GhostButton,
  PorCard,
  PrimaryButton,
  SectionStatusPill,
  formatDate,
} from '../../components/plan-of-record/porUi';
import { PorNarrativeMarkdown } from '../../components/plan-of-record/PorNarrativeMarkdown';
import { usePlanInstrumentNav } from '../../contexts/PlanInstrumentNavContext';

const STATUSES: PorSectionStatus[] = ['draft', 'in_review', 'approved'];

export default function PorSectionView({
  assembled,
  refresh,
}: {
  assembled: AssembledPor;
  refresh: () => Promise<void>;
}) {
  const navigate = useNavigate();
  const { porHref } = usePlanInstrumentNav();
  const { sectionType } = useParams<{ sectionType: string }>();
  const type = sectionType as PorSectionType;
  const { visibleSectionOrder } = usePorPageVisibility(assembled.id);

  const section = useMemo(
    () => assembled.sections.find((s) => s.section_type === type),
    [assembled.sections, type],
  );

  const [body, setBody] = useState(section?.body ?? '');
  const [status, setStatus] = useState<PorSectionStatus>(section?.status ?? 'draft');
  const [editing, setEditing] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Re-sync local state when navigating between sections.
  useEffect(() => {
    setBody(section?.body ?? '');
    setStatus(section?.status ?? 'draft');
    setError(null);
    setEditing(false);
  }, [section?.id, section?.body, section?.status]);

  if (!section) {
    return (
      <PorCard>
        <p className="text-sm text-[var(--text-ter)]">Unknown section.</p>
      </PorCard>
    );
  }

  const idx = visibleSectionOrder.indexOf(type);
  const prev = idx > 0 ? visibleSectionOrder[idx - 1] : null;
  const next = idx >= 0 && idx < visibleSectionOrder.length - 1 ? visibleSectionOrder[idx + 1] : null;
  const dirty = body !== (section.body ?? '') || status !== section.status;

  const handleSave = async () => {
    setBusy(true);
    setError(null);
    const { error: err } = await updateSection(section.id, { body: body.trim() || null, status });
    if (err) {
      setError(err);
      setBusy(false);
      return;
    }
    setBusy(false);
    setEditing(false);
    await refresh();
  };

  const handleDiscard = () => {
    setBody(section.body ?? '');
    setStatus(section.status ?? 'draft');
    setError(null);
    setEditing(false);
  };

  return (
    <div className="space-y-4 max-w-3xl">
      <header className="flex items-start justify-between">
        <div>
          <div className="text-[11px] font-mono uppercase tracking-[0.18em] text-[var(--text-ter)] dark:text-[var(--text-dim)]">
            Section {String(idx + 1).padStart(2, '0')} · The Record
          </div>
          <h1 className="text-2xl font-semibold text-[var(--text-pri)] dark:text-[var(--text-pri)] mt-0.5">{POR_SECTION_LABELS[type]}</h1>
          <p className="text-sm text-[var(--text-ter)] dark:text-[var(--text-sec)] mt-1">{POR_SECTION_PROMPTS[type]}</p>
        </div>
        <SectionStatusPill status={section.status} />
      </header>

      <PorCard>
        {/* ── Rendered view (default) ── */}
        {!editing && (
          <div>
            {body.trim() ? (
              <PorNarrativeMarkdown source={body} />
            ) : (
              <button
                type="button"
                onClick={() => setEditing(true)}
                className="w-full border-2 border-dashed border-[var(--rule)] dark:border-[var(--rule)] p-8 text-center hover:border-[var(--slate)] dark:hover:border-[var(--slate)] transition-colors group"
              >
                <p className="text-sm text-[var(--text-ter)] dark:text-[var(--text-ter)] group-hover:text-[var(--text-ter)] dark:group-hover:text-[var(--text-ter)]">
                  Nothing written yet — click to add content
                </p>
              </button>
            )}

            <div className="flex items-center justify-between mt-5 pt-4 border-t border-[var(--rule)] dark:border-[var(--rule)]">
              <p className="text-[11px] text-[var(--text-ter)] dark:text-[var(--text-dim)]">
                Last updated {formatDate(section.updated_at)}
              </p>
              <button
                type="button"
                onClick={() => setEditing(true)}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-[var(--text-sec)] dark:text-[var(--text-sec)] border border-[var(--rule)] dark:border-[var(--rule)] hover:bg-[var(--surface)] dark:hover:bg-[var(--hover-tint)] transition-colors"
              >
                <Pencil className="h-3 w-3" />
                Edit
              </button>
            </div>
          </div>
        )}

        {/* ── Edit mode ── */}
        {editing && (
          <div>
            <textarea
              autoFocus
              value={body}
              onChange={(e) => setBody(e.target.value)}
              rows={16}
              placeholder={POR_SECTION_PROMPTS[type]}
              spellCheck
              className="w-full min-h-[22rem] text-sm leading-relaxed border border-[var(--rule)] dark:border-[var(--rule)] bg-[var(--bg-raised)] dark:bg-[var(--surface)] text-[var(--text-pri)] dark:text-[var(--text-pri)] placeholder:text-[var(--text-ter)] dark:placeholder:text-[var(--text-ter)] px-3 py-2.5 focus:border-primary-400 dark:focus:border-primary-600 focus:ring-2 focus:ring-primary-100 dark:focus:ring-primary-950/80 focus:outline-none resize-y font-sans"
            />
            <p className="text-[11px] text-[var(--text-ter)] dark:text-[var(--text-ter)] mt-1.5">
              Markdown supported: headings, lists, emphasis, links, and tables. Line breaks are preserved.
            </p>

            <div className="flex items-center justify-between mt-4 pt-4 border-t border-[var(--rule)] dark:border-[var(--rule)]">
              <div className="flex items-center gap-2">
                <span className="text-xs font-semibold uppercase tracking-wide text-[var(--text-ter)] dark:text-[var(--text-ter)]">Status</span>
                <select
                  value={status}
                  onChange={(e) => setStatus(e.target.value as PorSectionStatus)}
                  className="text-sm border border-[var(--rule)] dark:border-[var(--rule)] bg-[var(--bg-raised)] dark:bg-[var(--surface)] text-[var(--text-pri)] dark:text-[var(--text-pri)] px-2 py-1.5 focus:outline-none focus:ring-2 focus:ring-indigo-100 dark:focus:ring-indigo-900"
                >
                  {STATUSES.map((s) => (
                    <option key={s} value={s}>
                      {s.replace('_', ' ')}
                    </option>
                  ))}
                </select>
              </div>
              <div className="flex items-center gap-3">
                {error && <span className="text-xs text-luminous-coral-700 dark:text-[var(--coral)]">{error}</span>}
                <button
                  type="button"
                  onClick={handleDiscard}
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-[var(--text-ter)] dark:text-[var(--text-sec)] border border-[var(--rule)] dark:border-[var(--rule)] hover:bg-[var(--surface)] dark:hover:bg-[var(--hover-tint)] transition-colors"
                >
                  <X className="h-3 w-3" />
                  Discard
                </button>
                <PrimaryButton onClick={handleSave} disabled={busy || !dirty}>
                  {busy ? 'Saving…' : 'Save section'}
                </PrimaryButton>
              </div>
            </div>
          </div>
        )}
      </PorCard>

      {/* Section-to-section navigation */}
      <div className="flex items-center justify-between">
        {prev ? (
          <GhostButton onClick={() => navigate(porHref(assembled.id, `section/${prev}`))}>
            <span className="inline-flex items-center gap-1">
              <ChevronLeft className="h-3.5 w-3.5" /> {POR_SECTION_LABELS[prev]}
            </span>
          </GhostButton>
        ) : (
          <span />
        )}
        {next ? (
          <GhostButton onClick={() => navigate(porHref(assembled.id, `section/${next}`))}>
            <span className="inline-flex items-center gap-1">
              {POR_SECTION_LABELS[next]} <ChevronRight className="h-3.5 w-3.5" />
            </span>
          </GhostButton>
        ) : (
          <span />
        )}
      </div>
    </div>
  );
}
